/**
 * Painel de demonstração — controles de APRESENTAÇÃO, nunca de dados.
 *
 * A versão anterior deste arquivo também fornecia a viagem e o conteúdo das
 * telas, vindos do dataset de exemplo. O efeito prático foi um defeito real:
 * uma conta nova, com uma viagem nova, abria a Home já preenchida com a viagem
 * de referência. Dado de exemplo como reserva de produto não é conveniência —
 * é informação errada apresentada como verdadeira.
 *
 * O que sobrou aqui, portanto, não descreve nenhuma viagem:
 *
 *  - `phase` **desloca o "hoje"** para exercitar antes/durante/depois **sobre
 *    as datas reais da viagem aberta**. `null` (o padrão) é o relógio de
 *    verdade;
 *  - `dataState` e `offline` forçam estados de tela — carregando, vazio, erro,
 *    sem rede — sem inventar conteúdo nenhum.
 *
 * Os dois só existem onde o painel pode aparecer (desenvolvimento e staging).
 * Em produção, `showDemoPanel()` é falso e ambos ficam presos no valor real:
 * não é só o painel que some, é o efeito dele.
 *
 * Papel e permissão saíram daqui de vez. Quem responde "o que eu posso fazer
 * nesta viagem" é o servidor, por `/trips/:id/me` — ver `TripContext`.
 */
import type { TripDates, TripPhase } from '@nv/core';
import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import { type DataState, instantForPhase } from '../features/home/model';
import { showDemoPanel } from './visibility';

interface DemoValue {
  /** Fase forçada, ou `null` para usar a data de hoje. */
  phase: TripPhase | null;
  setPhase: (phase: TripPhase | null) => void;
  dataState: DataState;
  setDataState: (state: DataState) => void;
  offline: boolean;
  setOffline: (offline: boolean) => void;
}

const DemoContext = createContext<DemoValue | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  // O padrão é o real. Um painel de demonstração que já começa demonstrando é
  // exatamente como este defeito nasceu.
  const [phase, setPhase] = useState<TripPhase | null>(null);
  const [dataState, setDataState] = useState<DataState>('ready');
  const [offline, setOffline] = useState(false);
  const permitido = showDemoPanel();

  const value = useMemo<DemoValue>(
    () => ({
      phase: permitido ? phase : null,
      setPhase,
      dataState: permitido ? dataState : 'ready',
      setDataState,
      offline: permitido ? offline : false,
      setOffline,
    }),
    [permitido, phase, dataState, offline],
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo(): DemoValue {
  const context = useContext(DemoContext);
  if (!context) throw new Error('useDemo precisa estar dentro de <DemoProvider>.');
  return context;
}

/**
 * O instante que as telas usam.
 *
 * Sem override, é agora. Com override, é um instante que coloca **esta**
 * viagem na fase pedida — derivado das datas dela, jamais de datas fixas.
 */
export function useAgora(trip: TripDates | null): Date {
  const { phase } = useDemo();
  const startDate = trip?.startDate;
  const endDate = trip?.endDate;

  return useMemo(() => {
    if (!phase || !trip) return new Date();
    try {
      return instantForPhase(trip, phase);
    } catch {
      return new Date();
    }
    // `trip` é recriado a cada render; o que muda de verdade são as datas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, startDate, endDate]);
}
